import {ErrorBoundary} from 'react-error-boundary';
import {ERROR, UNIT} from '@di-shared/const';
import React, {Suspense} from 'react';
import {EnergyUsageRecentProps} from '@di-shared/features/history/energy-usage/EnergyUsageRecent/index';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApiEu} from '@di-shared/api/di';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {parseDateWithFormat, subDate} from '@shared/dateUtils';
import EnergyPowerRecentGraph from '@di-shared/features/history/energy-usage/EnergyPowerRecentGraph';
import {formatNumberWithCommas} from '@di-shared/utils';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';

export default function ({vin}: EnergyUsageRecentProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner vin={vin} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: EnergyUsageRecentProps) {
  const {data: evRecentData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveEvStatsOfLastDrive), vin],
    queryFn: () => diApiEu.drivingHistory.retrieveEvStatsOfLastDrive({vin}),
    select: ({data}) => data,
  });

  const isRecentEnergyUsageNone =
    evRecentData.latestDriveDate === '' &&
    evRecentData.powerConsumption === 0 &&
    evRecentData.motorConsumptionRatio === 0 &&
    evRecentData.climateConsumptionRatio === 0 &&
    evRecentData.electricDeviceConsumptionRatio === 0 &&
    evRecentData.batteryManageConsumptionRatio === 0 &&
    evRecentData.avrMileage === 0 &&
    evRecentData.regenPower === 0;

  // No data 화면
  if (isRecentEnergyUsageNone) {
    return <None />;
  }

  // Default 화면
  return <Default vin={vin} />;
}

function None() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  const today = new Date();

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_mytrips_ev_title_02')}
        </h3>
        <span
          className="section_heading_caption"
          role="text"
          aria-label={`${t('ctn_mytirps_updated')} ${CldrDatetimeFormatter.format(subDate(today, {days: 1}), 'yMMMd')}`}
        >
          {t('ctn_mytirps_updated')} {CldrDatetimeFormatter.format(subDate(today, {days: 1}), 'yMMMd')}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header type_border">
          <div
            className="device_info_item"
            role="text"
            aria-label={`${t('ctn_mytrips_ev_energyuse')} ${0}${UNIT.KWH}`}
          >
            <h4
              className="device_info_key body_medium_b"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_energyuse')}
            </h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {0}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={0}
            electricDeviceRatio={0}
            climateRatio={0}
            batteryManageRatio={0}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_driving')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_electronics')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_climate')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_battery')}</span>
                <span className="legend_value">{0}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="section_container section_container_small">
        <div
          className="device_info_item"
          role="text"
          aria-label={`${t('ctn_mytrips_ev_average_energyuse')} ${0}${defaultEnergyEfficiencySymbol}`}
        >
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <h4
            className="device_info_key body_medium_b"
            aria-hidden={true}
          >
            {t('ctn_mytrips_ev_average_energyuse')}
          </h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {0}
              <span className="body_small_r">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </span>
        </div>
      </div>
      <div className="section_container section_container_small">
        <div
          className="device_info_item"
          role="text"
          aria-label={`${t('ctn_mytrips_ev_regenerative_01')} ${0}${defaultEnergyEfficiencySymbol}`}
        >
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4
            className="device_info_key body_medium_b"
            aria-hidden={true}
          >
            {t('ctn_mytrips_ev_regenerative_01')}
          </h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {0}
              <span className="body_small_r">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

function Default({vin}: EnergyUsageRecentProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {convertEnergyEfficiencyValue, convertEnergyEfficiencySymbol} = useUnitConverter();
  const {data: evRecentData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApiEu.drivingHistory.retrieveEvStatsOfLastDrive), vin],
    queryFn: () => diApiEu.drivingHistory.retrieveEvStatsOfLastDrive({vin}),
    select: ({data}) => data,
  });

  const verifiedRecentDrivingDate = parseDateWithFormat(evRecentData.latestDriveDate);
  if (!verifiedRecentDrivingDate) {
    throw new Error('latestDriveDate error');
  }

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_mytrips_ev_title_01')}
        </h3>
        <span
          className="section_heading_caption"
          role="text"
          aria-label={`${t('ctn_mytirps_updated')} ${CldrDatetimeFormatter.format(verifiedRecentDrivingDate, 'yMMMd')}`}
        >
          {t('ctn_mytirps_updated')} {CldrDatetimeFormatter.format(verifiedRecentDrivingDate, 'yMMMd')}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header type_border">
          <div
            className="device_info_item"
            role="text"
            aria-label={`${t('ctn_mytrips_ev_energyuse')} ${formatNumberWithCommas(evRecentData.powerConsumption)}${UNIT.KWH}`}
          >
            <h4
              className="device_info_key body_medium_b"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_energyuse')}
            </h4>
            <span
              className="device_info_val body_xlarge_b"
              aria-hidden={true}
            >
              <span className="device_info_amount body_large_b">
                {formatNumberWithCommas(evRecentData.powerConsumption)}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={evRecentData.motorConsumptionRatio}
            electricDeviceRatio={evRecentData.electricDeviceConsumptionRatio}
            climateRatio={evRecentData.climateConsumptionRatio}
            batteryManageRatio={evRecentData.batteryManageConsumptionRatio}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_driving')} ${evRecentData.motorConsumptionRatio}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_driving')}</span>
                <span className="legend_value">{evRecentData.motorConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_electronics')} ${evRecentData.electricDeviceConsumptionRatio}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_electronics')}</span>
                <span className="legend_value">{evRecentData.electricDeviceConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_climate')} ${evRecentData.climateConsumptionRatio}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_climate')}</span>
                <span className="legend_value">{evRecentData.climateConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_battery')} ${evRecentData.batteryManageConsumptionRatio}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_battery')}</span>
                <span className="legend_value">{evRecentData.batteryManageConsumptionRatio}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div
        className="section_container section_container_small"
        role="text"
        aria-label={`${t('ctn_mytrips_ev_average_energyuse')} ${formatNumberWithCommas(convertEnergyEfficiencyValue(evRecentData.avrMileage))}${convertEnergyEfficiencySymbol(evRecentData.avrMileage)}`}
      >
        <div
          className="device_info_item"
          aria-label={`${t('ctn_mytrips_ev_average_energyuse')} ${formatNumberWithCommas(convertEnergyEfficiencyValue(evRecentData.avrMileage))}${convertEnergyEfficiencySymbol(evRecentData.avrMileage)}`}
        >
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <h4
            className="device_info_key body_medium_b"
            aria-hidden={true}
          >
            {t('ctn_mytrips_ev_average_energyuse')}
          </h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {formatNumberWithCommas(convertEnergyEfficiencyValue(evRecentData.avrMileage))}
              <span className="body_small_r">{convertEnergyEfficiencySymbol(evRecentData.avrMileage)}</span>
            </span>
          </span>
        </div>
      </div>
      <div
        className="section_container section_container_small"
        role="text"
      >
        <div
          className="device_info_item"
          aria-label={`${t('ctn_mytrips_ev_regenerative_01')} ${formatNumberWithCommas(evRecentData.regenPower)}${UNIT.KWH}`}
        >
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4
            className="device_info_key body_medium_b"
            aria-hidden={true}
          >
            {t('ctn_mytrips_ev_regenerative_01')}
          </h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {formatNumberWithCommas(evRecentData.regenPower)}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();

  return (
    <section
      className="section"
      aria-hidden="true"
    >
      <div className="section_heading type_main">
        <button
          type="button"
          className="btn_monthly body_medium_b skeleton"
          style={{width: '160px', height: '20px'}}
        />
        <h3
          className="section_heading_title skeleton"
          style={{width: '200px', height: '28px'}}
        />
        <span
          className="section_heading_caption skeleton"
          style={{width: '160px', height: '20px'}}
        />
      </div>
      <div className="section_container skeleton">
        <div className="section_container_header type_border">
          <div className="device_info_item">
            <h4 className="device_info_key body_medium_b">{t('ctn_mytrips_ev_energyuse')}</h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={0}
            electricDeviceRatio={0}
            climateRatio={0}
            batteryManageRatio={0}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_driving')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_electronics')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_climate')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_battery')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="section_container section_container_small skeleton">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('ctn_mytrips_ev_average_energyuse')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {ERROR.DASH}
              <span className="body_small_r">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </span>
        </div>
      </div>
      <div className="section_container section_container_small skeleton">
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4 className="device_info_key body_medium_b">{t('ctn_mytrips_ev_regenerative_01')}</h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {ERROR.DASH}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {defaultEnergyEfficiencySymbol} = useUnitConverter();
  const today = new Date();

  return (
    <section className="section">
      <div className="section_heading type_main">
        <h3
          className="section_heading_title"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_mytrips_ev_title_01')}
        </h3>
        <span
          className="section_heading_caption"
          role="text"
          aria-label={`${t('ctn_mytirps_updated')} ${CldrDatetimeFormatter.format(subDate(today, {days: 1}), 'yMMMd')}`}
        >
          {t('ctn_mytirps_updated')} {CldrDatetimeFormatter.format(subDate(today, {days: 1}), 'yMMMd')}
        </span>
      </div>
      <div className="section_container">
        <div className="section_container_header type_border">
          <div
            className="device_info_item"
            role="text"
            aria-label={`${t('ctn_mytrips_ev_energyuse')} ${ERROR.DASH}${UNIT.KWH}`}
          >
            <h4
              className="device_info_key body_medium_b"
              aria-hidden={true}
            >
              {t('ctn_mytrips_ev_energyuse')}
            </h4>
            <span className="device_info_val body_xlarge_b">
              <span className="device_info_amount body_large_b">
                {ERROR.DASH}
                <span className="body_small_r">{UNIT.KWH}</span>
              </span>
            </span>
          </div>
        </div>
        <div className="electricity_graph_area">
          <EnergyPowerRecentGraph
            motorRatio={0}
            electricDeviceRatio={0}
            climateRatio={0}
            batteryManageRatio={0}
          />
          <div className="electricty_graph_info">
            <ul className="graph_legend_list">
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_driving')} ${ERROR.DASH}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--graph-gauge-active)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_driving')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_electronics')} ${ERROR.DASH}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--blue-custom)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_electronics')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_climate')} ${ERROR.DASH}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-mint)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_climate')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
              <li
                className="graph_legend_item"
                role="text"
                aria-label={`${t('ctn_mytrips_ev_energyuse_battery')} ${ERROR.DASH}${UNIT.PERCENT}`}
              >
                <span
                  className="legend_color"
                  style={{backgroundColor: 'var(--di-purple)'}}
                />
                <span className="legend_name">{t('ctn_mytrips_ev_energyuse_battery')}</span>
                <span className="legend_value">{ERROR.DASH}</span>
                <span className="legend_unit">{UNIT.PERCENT}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div
        className="section_container section_container_small"
        role="text"
        aria-label={`${t('ctn_mytrips_ev_average_energyuse')} ${ERROR.DASH}${defaultEnergyEfficiencySymbol}`}
      >
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_battery_24px" />
          </span>
          <h4
            className="device_info_key body_medium_b"
            aria-hidden={true}
          >
            {t('ctn_mytrips_ev_average_energyuse')}
          </h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {ERROR.DASH}
              <span className="body_small_r">{defaultEnergyEfficiencySymbol}</span>
            </span>
          </span>
        </div>
      </div>
      <div
        className="section_container section_container_small"
        role="text"
        aria-label={`${t('ctn_mytrips_ev_regenerative_01')} ${ERROR.DASH}${UNIT.KWH}`}
      >
        <div className="device_info_item">
          <span className="device_info_icon">
            <span className="ic ic_reservation_charge_24px" />
          </span>
          <h4
            className="device_info_key body_medium_b"
            aria-hidden={true}
          >
            {t('ctn_mytrips_ev_regenerative_01')}
          </h4>
          <span className="device_info_val body_xlarge_b">
            <span className="device_info_amount body_large_b">
              {ERROR.DASH}
              <span className="body_small_r">{UNIT.KWH}</span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}
